import React, { useMemo } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import type { CapabilityMapDataset, DomainMode, MarketStatus } from "./types";

interface Props {
  dataset: CapabilityMapDataset;
  domainMode: DomainMode;
  marketStatusFilter: MarketStatus | null;
  onFilterByStatus: (status: MarketStatus | null) => void;
}

const STATUS_COLORS: Record<MarketStatus, { bg: string; label: string }> = {
  crowded: { bg: "#b71c1c", label: "Crowded (4+ cos)" },
  moderate: { bg: "#f57f17", label: "Moderate (2–3 cos)" },
  open: { bg: "#1b5e20", label: "Open (0–1 cos)" },
};

function deriveStatus(count: number): MarketStatus {
  if (count >= 4) return "crowded";
  if (count >= 2) return "moderate";
  return "open";
}

const CapabilityMapSummaryBar: React.FC<Props> = ({
  dataset,
  domainMode,
  marketStatusFilter,
  onFilterByStatus,
}) => {
  const counts = useMemo(() => {
    const result: Record<MarketStatus, number> = {
      crowded: 0,
      moderate: 0,
      open: 0,
    };
    for (const cluster of dataset.clusters) {
      for (const sc of cluster.subCapabilities) {
        const companyCount = dataset.companies.filter(
          (c) =>
            c.capabilitiesCovered.includes(sc.id) &&
            (domainMode === "all" ||
              c.domains.includes(domainMode as "defense" | "industrial")),
        ).length;
        result[deriveStatus(companyCount)]++;
      }
    }
    return result;
  }, [dataset, domainMode]);

  const totalDemand = useMemo(() => dataset.tenders.length, [dataset.tenders]);
  const highDemandCaps = useMemo(
    () => dataset.systemCapabilities.filter((sc) => sc.demandCount >= 2).length,
    [dataset.systemCapabilities],
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 0.75,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.default",
        flexWrap: "wrap",
      }}
    >
      <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.5 }}>
        Market density:
      </Typography>
      {(["crowded", "moderate", "open"] as MarketStatus[]).map((status) => (
        <Tooltip
          key={status}
          title={`Click to filter to ${status} sub-capabilities only`}
        >
          <Chip
            label={`${counts[status]} ${STATUS_COLORS[status].label}`}
            size="small"
            onClick={() =>
              onFilterByStatus(marketStatusFilter === status ? null : status)
            }
            sx={{
              bgcolor: STATUS_COLORS[status].bg,
              color: "#fff",
              fontWeight: marketStatusFilter === status ? 800 : 500,
              opacity:
                marketStatusFilter && marketStatusFilter !== status ? 0.4 : 1,
              fontSize: "0.65rem",
              height: 22,
              cursor: "pointer",
              border: marketStatusFilter === status ? "2px solid #fff" : "none",
              transition: "opacity 0.2s",
            }}
          />
        </Tooltip>
      ))}

      <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5 }} />

      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Demand:
      </Typography>
      <Chip
        label={`${totalDemand} tender${totalDemand !== 1 ? "s" : ""}`}
        size="small"
        sx={{
          bgcolor: "#5D4037",
          color: "#fff",
          fontSize: "0.65rem",
          height: 22,
        }}
      />
      <Chip
        label={`${highDemandCaps} high-demand capabilities`}
        size="small"
        sx={{
          bgcolor: "#b71c1c",
          color: "#fff",
          fontSize: "0.65rem",
          height: 22,
        }}
      />
    </Box>
  );
};

export default CapabilityMapSummaryBar;
